import {
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	linkedSignal,
	OnDestroy,
	signal,
	untracked,
	viewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { IconNames } from '@icon/assets';
import {
	UiButtonComponent,
	UiCheckboxComponent,
	UiEmptyStateComponent,
	UIIconComponent,
	UiInputSearchComponent,
	UiLoadingComponent
} from '@design-system';
import { convertToPersianText, SwipeRightLeftDirective } from '@tools';
import { catchError, distinctUntilChanged, map, of, startWith, switchMap, tap } from 'rxjs';
import { OrderCancelBottomSheetComponent } from './components/order-cancel-bottom-sheet/order-cancel-bottom-sheet.component';
import {
	OrderDetailBottomSheetComponent,
	OrderDetailLastPrice
} from './components/order-detail-bottom-sheet/order-detail-bottom-sheet.component';
import { OrderSettingsBottomSheetComponent } from './components/order-settings-bottom-sheet/order-settings-bottom-sheet.component';
import { OrderSideEnum } from '../../models/order-response.model';
import { isOpenOrder, Order } from '../../models/order.model';
import { OrderCardItemComponent } from './components/order-card-item/order-card-item.component';
import { OrdersStore } from './data-access/orders.store';
import { OrdersHeaderEventConsumerService } from './events/orders-header-event-consumer.service';
import { OrdersHeaderEventProducerService } from './events/orders-header-event-producer.service';
import { HeaderItemToHeader } from './events/orders-header-events.model';
import {
	DEFAULT_ORDERS_FILTER_OBJECT,
	OrdersFilterObject,
	readOrdersFiltersFromSession,
	writeOrdersFiltersToSession
} from './orders-filter.storage';
import { PrivateMessageEventConsumerService } from '../../events/private-message/private-message-event.consumer.service';
import { MarketDataStore } from '../../core/market-data';
import { MarketViewMessage } from '../market/services/market-view.model';
import { MarketViewService } from '../market/services/market-view.service';
import { OrdersActionsBottomSheetComponent } from './components/orders-actions-bottom-sheet/orders-actions-bottom-sheet.component';
import { OrdersAction } from './components/orders-actions-bottom-sheet/orders-actions-bottom-sheet.model';
import { ToastService } from '../../services/toast.service';

/**
 * The order that took `previous`'s place in a reload. An edit comes back from `/orders` as a
 * new row under a new `orderId`, with the old row gone — so the replacement is the one order
 * on the same symbol and side the list didn't hold before. More than one such order is
 * ambiguous, and following the wrong one would be worse than letting the sheet go.
 */
function findReplacementOrder(previous: Order, orders: readonly Order[], knownIds: ReadonlySet<number>): Order | null {
	const candidates = orders.filter(
		order =>
			!knownIds.has(order.orderId) && order.symbolIsin === previous.symbolIsin && order.side === previous.side
	);
	return candidates.length === 1 ? candidates[0] : null;
}

/**
 * Market-view ticks only: the REST snapshot's price lags the market, so the row waits for the
 * first tick rather than showing a stale figure that then jumps. Null until a trade is reported.
 */
function toLastPrice(live: MarketViewMessage): OrderDetailLastPrice | null {
	if (!live.ltp) {
		return null;
	}
	const change = live.ltp - live.lsp;
	return { price: live.ltp, change, changePercent: live.lsp ? (change / live.lsp) * 100 : 0 };
}

/** Changes exactly when the order's fills do, so the breakdown is refetched only then. */
function fillKey(order: Order): string {
	return `${order.orderId}:${order.executedVolume}`;
}

@Component({
	selector: 'app-orders',
	imports: [
		OrderCardItemComponent,
		OrderCancelBottomSheetComponent,
		OrderDetailBottomSheetComponent,
		OrderSettingsBottomSheetComponent,
		OrdersActionsBottomSheetComponent,
		SwipeRightLeftDirective,
		UiButtonComponent,
		UiInputSearchComponent,
		UIIconComponent,
		UiEmptyStateComponent,
		UiLoadingComponent,
		UiCheckboxComponent
	],
	templateUrl: './orders.component.html',
	providers: [OrdersHeaderEventProducerService, OrdersHeaderEventConsumerService, MarketViewService],
	host: {
		class: 'block h-full',
		'(document:click)': 'onDocumentClick($event)'
	}
})
export class OrdersComponent implements OnDestroy {
	private readonly ordersHeaderProducer = inject(OrdersHeaderEventProducerService);
	private readonly ordersHeaderConsumer = inject(OrdersHeaderEventConsumerService);
	private readonly ordersStore = inject(OrdersStore);
	private readonly privateMessages = inject(PrivateMessageEventConsumerService);
	private readonly router = inject(Router);
	private readonly marketData = inject(MarketDataStore);
	private readonly marketViewService = inject(MarketViewService);
	private readonly toastService = inject(ToastService);

	private readonly storedFilters = readOrdersFiltersFromSession();

	readonly symbol = input(null, { transform: (value: string | null | undefined) => value ?? null });

	readonly managesHeader = input(true, { transform: (value: boolean | undefined) => value ?? true });

	readonly orders = this.ordersStore.orders;
	readonly loading = this.ordersStore.loading;
	readonly error = this.ordersStore.error;
	readonly filterPanel = signal(false);
	readonly filterObject = signal<OrdersFilterObject>(this.storedFilters ?? DEFAULT_ORDERS_FILTER_OBJECT);
	readonly selectedOrderId = signal<number | null>(null);

	/** Set when the delete came from the detail sheet, so dismissing brings that sheet back. */
	private readonly returnToDetail = signal(false);

	/** The fill state the open sheet's «ریز معاملات» was last fetched for — see `fillKey`. */
	private tradesFetchedFor: string | null = null;

	/** Order ids the previous `/orders` snapshot held — what tells a replacement row apart. */
	private knownOrderIds: ReadonlySet<number> = new Set();

	/** Set when the delete came from the bulk-delete toolbar, so a cancel closes that toolbar. */
	private readonly deletingFromToolbar = signal(false);

	private readonly ordersList = viewChild<ElementRef<HTMLElement>>('ordersList');
	readonly toolbarActive = signal(false);
	readonly selectedOrderIds = signal<number[]>([]);

	private readonly detailSheet = viewChild<OrderDetailBottomSheetComponent>('detailSheet');
	private readonly cancelSheet = viewChild<OrderCancelBottomSheetComponent>('cancelSheet');
	private readonly actionsSheet = viewChild<OrdersActionsBottomSheetComponent>('actionsSheet');
	private readonly settingsSheet = viewChild<OrderSettingsBottomSheetComponent>('settingsSheet');

	/** The selected order as the list holds it right now; null while the list doesn't. */
	private readonly liveSelectedOrder = computed(() => {
		const id = this.selectedOrderId();
		return id ? (this.orders().find(order => order.orderId === id) ?? null) : null;
	});

	/**
	 * Derived from the orders list rather than stored as a snapshot, so fills that
	 * arrive while the sheet is open flow straight through to the open sheet.
	 *
	 * A reload that lands without the order keeps its last-seen version for as long as the
	 * sheet needs to move on to the row that replaced it — or to close — so it never blanks.
	 */
	readonly selectedOrder = linkedSignal<{ id: number | null; live: Order | null }, Order | null>({
		source: () => ({ id: this.selectedOrderId(), live: this.liveSelectedOrder() }),
		computation: ({ id, live }, previous) => live ?? (previous?.value?.orderId === id ? previous.value : null)
	});

	/** The symbol to quote in the detail sheet — only while it is on screen, so the stream closes with it. */
	private readonly quotedIsin = computed(() =>
		this.detailSheet()?.isOpen() ? (this.selectedOrder()?.symbolIsin ?? null) : null
	);

	/**
	 * The quoted symbol's last traded price and change against the previous close, from its
	 * market-view stream. The symbol snapshot is asked only for the contract id the stream is keyed on.
	 */
	private readonly liveLastPrice = toSignal(
		toObservable(this.quotedIsin).pipe(
			distinctUntilChanged(),
			// Tagged with its isin: the stream trails `quotedIsin` by a tick, and the sheet must not
			// show the previous order's quote under the next one in between.
			switchMap(isin =>
				!isin
					? of(null)
					: this.marketData.getSymbol(isin).pipe(
							switchMap(symbol =>
								!symbol
									? of(null)
									: this.marketViewService
											.connectSingleContract(symbol.contractId)
											.pipe(map(toLastPrice))
							),
							// A failed symbol snapshot call or a dropped stream only hides the row — an
							// error reaching `toSignal` would make every later read throw.
							catchError(() => of(null)),
							startWith(null),
							map(quote => ({ isin, quote }))
						)
			)
		),
		{ initialValue: null }
	);

	/** Holds the last quote once the sheet starts closing, so the row doesn't vanish mid-animation. */
	readonly selectedOrderLastPrice = linkedSignal<
		{ isin: string | null; tagged: { isin: string; quote: OrderDetailLastPrice | null } | null },
		OrderDetailLastPrice | null
	>({
		source: () => ({ isin: this.quotedIsin(), tagged: this.liveLastPrice() }),
		computation: ({ isin, tagged }, previous) => {
			if (!isin) {
				return previous?.value ?? null;
			}
			return tagged?.isin === isin ? tagged.quote : null;
		}
	});

	readonly filterIcon = computed(() => (this.filterPanel() ? 'filter-off' : 'filter') as IconNames);
	readonly redLightIcon = computed(() => (this.filterObject().showRed ? 'circle' : 'circle-empty') as IconNames);
	readonly greenLightIcon = computed(() => (this.filterObject().showGreen ? 'circle' : 'circle-empty') as IconNames);
	readonly blueLightIcon = computed(() => (this.filterObject().showDone ? 'circle' : 'circle-empty') as IconNames);

	readonly emptyStateTitle = computed(() => {
		if (this.loading() || this.error() || this.filteredOrders().length > 0) {
			return '';
		}

		const { showGreen, showRed, showDone, searchInput } = this.filterObject();
		const isFilterApplied = !showGreen || !showRed || !showDone || searchInput.length > 0;
		return isFilterApplied && !this.symbol() ? 'با فیلترهای وارد شده سفارشی یافت نشد' : 'سفارشی وجود ندارد';
	});

	readonly orderHistoryLabel = computed(() => (this.symbol() ? 'تاریخچه سفارش‌های این نماد' : 'تاریخچه سفارش‌ها'));

	readonly filteredOrders = computed(() => {
		const { showGreen, showRed, showDone, searchInput } = this.filterObject();
		return this.orders().filter(order => {
			if (this.symbol()) {
				return true;
			}
			// Mirrors `OmsOrderListFacade.toggleFilter$`: each light off subtracts its own group
			// and nothing else, so the groups overlap — an executed buy goes with green off as
			// well as with blue off, and both sides off legitimately empties the list.
			if (!showGreen && order.side === OrderSideEnum.Buy) {
				return false;
			}
			if (!showRed && order.side === OrderSideEnum.Sell) {
				return false;
			}
			if (!showDone && order.isExecuted) {
				return false;
			}
			if (
				searchInput.length > 0 &&
				!(order.symbolName ?? '').includes(searchInput) &&
				!(order.symbolShortName ?? '').includes(searchInput)
			) {
				return false;
			}
			return true;
		});
	});

	readonly selectableOrderIds = computed(() =>
		this.filteredOrders()
			.filter(isOpenOrder)
			.map(order => order.orderId)
	);

	/**
	 * `selectedOrderIds` isn't pruned as orders leave `selectableOrderIds` (e.g. a filter hides a
	 * selected order), so it can hold ids that are no longer selectable. Intersecting with
	 * `selectableOrderIds()` keeps every read (count label, delete/select-all state) in sync
	 * without needing an effect to prune the raw signal.
	 */
	readonly effectiveSelectedOrderIds = computed(() => {
		const selectable = new Set(this.selectableOrderIds());
		return this.selectedOrderIds().filter(id => selectable.has(id));
	});

	readonly allOrdersSelected = computed(
		() =>
			this.effectiveSelectedOrderIds().length > 0 &&
			this.effectiveSelectedOrderIds().length === this.selectableOrderIds().length
	);

	readonly deleteToolbarActive = computed(() => this.effectiveSelectedOrderIds().length > 0);

	/** The selected orders themselves, for a confirmation that is handed orders rather than ids. */
	readonly selectedOrders = computed(() => {
		const selected = new Set(this.effectiveSelectedOrderIds());
		return this.orders().filter(order => selected.has(order.orderId));
	});

	/**
	 * Latin digits, as everywhere else: «نمایش اعداد فارسی» is rendered by the
	 * `persian-digits` font feature rather than written into the text.
	 */
	readonly selectedOrdersCountLabel = computed(() =>
		this.effectiveSelectedOrderIds().length > 0 ? this.effectiveSelectedOrderIds().length.toString() : ''
	);

	constructor() {
		this.reloadOnServerRequest();

		effect(() => {
			const symbol = this.symbol();
			untracked(() => this.ordersStore.loadOrders({ symbol }));
		});

		effect(() => {
			writeOrdersFiltersToSession(this.filterObject());
		});

		// The open sheet follows its order through a reload: when the order's id is gone, the
		// sheet moves on to the row that replaced it and redraws from that. With no replacement
		// the order is gone for good — deleted elsewhere — and so is the sheet.
		effect(() => {
			const orders = this.orders();
			const knownIds = this.knownOrderIds;
			this.knownOrderIds = new Set(orders.map(order => order.orderId));

			untracked(() => {
				const id = this.selectedOrderId();
				const shown = this.selectedOrder();
				const detailOpen = this.detailSheet()?.isOpen() ?? false;
				if (id === null || !shown || !detailOpen || orders.some(order => order.orderId === id)) {
					return;
				}

				const replacement = findReplacementOrder(shown, orders, knownIds);
				if (replacement) {
					this.selectedOrderId.set(replacement.orderId);
				} else {
					this.dropDetailSheetAndSelection();
				}
			});
		});

		// A reload brings the new status and executed volume, but not the fills behind them —
		// those come from `/trades` only. So a fill landing under the open sheet refetches them.
		effect(() => {
			const order = this.liveSelectedOrder();
			const detailOpen = this.detailSheet()?.isOpen() ?? false;
			if (!order || !detailOpen || fillKey(order) === this.tradesFetchedFor) {
				return;
			}
			this.tradesFetchedFor = fillKey(order);
			untracked(() => this.ordersStore.loadTrades(order.orderId));
		});

		effect(() => {
			if (!this.managesHeader()) {
				return;
			}

			const filterIcon = this.filterIcon();
			const isLoading = this.loading();
			const headerLeftSectionItems: HeaderItemToHeader[] = [
				{ iconName: 'refresh', eventId: 'refresh', ariaLabel: 'بروزرسانی', reloading: isLoading },
				{ iconName: filterIcon, eventId: 'filter', ariaLabel: 'فیلتر' }
			];

			if (!this.toolbarActive()) {
				headerLeftSectionItems.push({
					iconName: 'menu-vertical',
					eventId: 'actions',
					ariaLabel: 'عملیات بیشتر'
				});
			}

			this.ordersHeaderProducer.configureOrdersHeader({
				headerLeftSectionItems
			});
		});

		toSignal(
			this.ordersHeaderConsumer.headerAction$.pipe(
				tap(event => {
					if (!this.managesHeader()) {
						return;
					}

					switch (event?.data?.eventId) {
						case 'filter':
							this.toggleFilterPanel();
							break;
						case 'refresh':
							this.reloadOrders();
							break;
						case 'actions':
							this.openActionsSheet();
							break;
					}

					if (event) {
						this.ordersHeaderProducer.clearEventSnapshot(event.id);
					}
				})
			)
		);
	}

	onSearchChange(value: string): void {
		this.filterObject.update(current => ({
			...current,
			searchInput: value ? convertToPersianText(value) : ''
		}));
	}

	toggleColorFilter(colorFilterName: 'showRed' | 'showGreen' | 'showDone'): void {
		this.filterObject.update(current => ({
			...current,
			[colorFilterName]: !current[colorFilterName]
		}));
	}

	toggleFilterPanel(): void {
		this.filterPanel.update(isOpen => !isOpen);
		// Not just hiding the toolbar: the filter icon stays reachable while it's open, so without
		// clearing the selection too, reopening bulk-delete would resurrect stale checkmarks.
		this.closeToolbar();
	}

	onRetry(): void {
		this.reloadOrders();
	}

	onOrderHistoryClicked(): void {
		const symbol = this.symbol();
		this.router.navigate(['/orders/history'], symbol ? { queryParams: { symbol } } : {});
	}

	private reloadOrders({ silent = false }: { silent?: boolean } = {}): void {
		this.ordersStore.loadOrders({ silent, symbol: this.symbol() });
	}

	/**
	 * The swipe directive only snaps a row back for clicks outside the whole list, so a tap
	 * on another card or on the empty space below the last one would leave a row swiped open.
	 * Anything outside the card that owns the row closes it — a tap on that card's own actions
	 * is left alone, so swipe-delete keeps its row open while the confirmation is up.
	 */
	onDocumentClick(event: MouseEvent): void {
		const list = this.ordersList()?.nativeElement;
		if (!list) {
			return;
		}

		const clickedCard = (event.target as HTMLElement | null)?.closest('app-order-card-item') ?? null;
		list.querySelectorAll<HTMLElement>('[swipeable]').forEach(row => {
			if (row.closest('app-order-card-item') === clickedCard || !this.isSwipedOpen(row)) {
				return;
			}
			row.style.transitionDuration = '0.3s';
			row.style.transform = 'translate3d(0px, 0px, 0px)';
		});
	}

	/** A row rests at an untouched or zeroed transform; anything else means it is swiped open. */
	private isSwipedOpen(row: HTMLElement): boolean {
		const transform = row.style.transform;
		return transform !== '' && !transform.startsWith('translate3d(0px');
	}

	onOrderDetailRequested(order: Order): void {
		this.selectedOrderId.set(order.orderId);
		// The fills back the sheet's «ریز معاملات» section and its executed price, and they
		// belong to one order — so they're fetched here rather than for every card in the list.
		this.tradesFetchedFor = fillKey(order);
		this.ordersStore.loadTrades(order.orderId);
		this.detailSheet()?.open();
	}

	/**
	 * Reloads the list when the server says it is stale.
	 *
	 * The event is all this component knows: what asked for it, and how it reached
	 * us, are somebody else's concern. Silent, because nobody here pressed
	 * anything — a spinner blanking the list for a refresh the user did not ask
	 * for reads as a glitch, and a dropped request is better answered by the
	 * orders already on screen than by an error replacing them.
	 */
	private reloadOnServerRequest(): void {
		this.privateMessages.ordersReloadRequested$
			.pipe(takeUntilDestroyed())
			.subscribe(() => this.reloadOrders({ silent: true }));
	}

	/**
	 * Swipe-delete on a card confirms without the detail sheet, so backing out lands back on
	 * the card's own actions — the row is still swiped open behind the confirmation.
	 */
	onOrderDeleteRequested(order: Order): Promise<void> {
		this.selectedOrderId.set(order.orderId);
		this.returnToDetail.set(false);
		// The order goes with the call: `selectedOrder` was just re-derived here, but the sheet's
		// own copy of it only lands on the next change detection — too late for a confirmation
		// that is switched off and sends the delete before this handler returns.
		return this.cancelSheet()?.open(order) ?? Promise.resolve();
	}

	/**
	 * Delete from the detail sheet: that sheet closes first — otherwise the confirmation would
	 * queue behind it — and comes back if the user backs out.
	 */
	async onDetailCancelRequested(): Promise<void> {
		const cancelSheet = this.cancelSheet();
		if (!cancelSheet) {
			return;
		}

		if (!cancelSheet.confirmationEnabled()) {
			this.returnToDetail.set(false);
			await cancelSheet.open(this.selectedOrder());
			return;
		}

		this.returnToDetail.set(true);
		await (this.detailSheet()?.close() ?? Promise.resolve());
		await cancelSheet.open(this.selectedOrder());
	}

	private async dropDetailSheetAndSelection(): Promise<void> {
		this.returnToDetail.set(false);
		await (this.detailSheet()?.close() ?? Promise.resolve());
		this.selectedOrderId.set(null);
	}

	/** Backing out of a bulk delete leaves the toolbar as it was, selection included. */
	onCancelDismissed(): Promise<void> {
		this.deletingFromToolbar.set(false);
		if (!this.returnToDetail()) {
			return Promise.resolve();
		}
		this.returnToDetail.set(false);

		// The live list, not the held copy: an order deleted meanwhile has nothing to come back to.
		if (!this.liveSelectedOrder()) {
			this.selectedOrderId.set(null);
			return Promise.resolve();
		}

		return this.detailSheet()?.open() ?? Promise.resolve();
	}

	/** The order is gone, so there is nothing left to come back to. */
	async onOrderCancelled(): Promise<void> {
		await this.dropDetailSheetAndSelection();

		// The selection it was made from is spent, so the toolbar goes with it — a batch of one
		// lands here too, which is why this follows the origin rather than the order count.
		if (this.deletingFromToolbar()) {
			this.deletingFromToolbar.set(false);
			this.closeToolbar();
		}
	}

	openActionsSheet(): void {
		this.actionsSheet()?.open();
	}

	async onActionSelected(action: OrdersAction): Promise<void> {
		switch (action) {
			case 'today-activity':
				await this.router.navigate(['/orders/today-activity']);
				break;
			case 'settings':
				await (this.settingsSheet()?.open() ?? Promise.resolve());
				break;
			case 'bulk-delete': {
				if (this.selectableOrderIds().length === 0) {
					this.toastService.warning('هیچ سفارش قابل حذفی وجود ندارد');
					return;
				}
				this.toolbarActive.set(true);
				break;
			}
		}
	}

	isOrderSelected(orderId: number): boolean {
		return this.effectiveSelectedOrderIds().includes(orderId);
	}

	checkboxChanged(orderId: number): void {
		if (this.isOrderSelected(orderId)) {
			this.selectedOrderIds.update(ids => ids.filter(id => id !== orderId));
		} else {
			this.selectedOrderIds.update(ids => [...ids, orderId]);
		}
	}

	onHoldEvent(orderId: number): void {
		const order = this.orders().find(o => o.orderId === orderId);
		if (!order || !isOpenOrder(order)) {
			return;
		}
		this.checkboxChanged(orderId);
		this.toolbarActive.set(true);
	}

	toggleSelectAll(): void {
		if (this.allOrdersSelected()) {
			this.deselectAll();
		} else {
			this.selectAll();
		}
	}

	selectAll(): void {
		this.selectedOrderIds.set([...this.selectableOrderIds()]);
	}

	deselectAll(): void {
		this.selectedOrderIds.set([]);
	}

	closeToolbar(): void {
		this.deselectAll();
		this.toolbarActive.set(false);
	}

	/**
	 * Bulk delete goes through the same confirmation as the single-order deletes, so «تاییدیه
	 * سفارش» gates it too — the sheet sends the batch itself and reports how it went.
	 *
	 * Nothing here waits on the exchange: the sheet hands the batch over and closes, and the
	 * answer comes back as a toast, so the toolbar has no in-flight state of its own to show.
	 */
	async onDeleteSelectedOrdersClicked(): Promise<void> {
		const orders = this.selectedOrders();
		if (orders.length === 0) {
			return;
		}

		this.returnToDetail.set(false);
		this.deletingFromToolbar.set(true);

		await (this.cancelSheet()?.open(orders) ?? Promise.resolve());
	}

	ngOnDestroy(): void {
		this.closeToolbar();
		this.ordersStore.reset();
		if (this.managesHeader()) {
			this.ordersHeaderProducer.resetHeader();
		}
	}
}
